import {
  ArrowLeft,
  Brain,
  Heart,
  Moon,
  Wind,
  Thermometer,
  Activity,
  Info,
  TrendingDown,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const StressScoreDetail = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Stress Score</h1>
        </div>

        {/* Current Stress Score */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-warning/20 flex h-20 w-20 items-center justify-center rounded-2xl">
              <Brain className="text-warning h-10 w-10" />
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm text-muted-foreground">
                Current Stress Level
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-black text-foreground">6.2</span>
                <span className="text-xl text-muted-foreground">/ 10</span>
              </div>
            </div>
          </div>

          {/* Stress Progress Bar */}
          <div className="mt-4">
            <div className="mb-2 flex justify-between text-xs text-muted-foreground">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
            </div>
            <div className="from-success via-warning relative h-3 rounded-full bg-gradient-to-r to-destructive">
              <div
                className="border-warning absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 bg-white shadow-lg"
                style={{ left: `${62}%`, transform: "translate(-50%, -50%)" }}
              />
            </div>
          </div>
        </Card>

        {/* Stress Ranges */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">Stress Levels</h3>
          <div className="space-y-2">
            <div className="bg-success/10 flex items-center justify-between rounded-lg p-2">
              <span className="text-sm font-medium text-foreground">
                Low Stress
              </span>
              <span className="text-sm text-muted-foreground">0 - 3.9</span>
            </div>
            <div className="bg-warning/10 flex items-center justify-between rounded-lg p-2">
              <span className="text-sm font-medium text-foreground">
                Moderate Stress
              </span>
              <span className="text-sm text-muted-foreground">4.0 - 6.9</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-destructive/10 p-2">
              <span className="text-sm font-medium text-foreground">
                High Stress
              </span>
              <span className="text-sm text-muted-foreground">7.0 - 10</span>
            </div>
          </div>
        </Card>

        {/* How Stress is Calculated */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">
              How is Stress Calculated?
            </h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              Your stress score is calculated using a combination of{" "}
              <strong className="text-foreground">physiological signals</strong>{" "}
              collected from your wearable device during sleep and throughout
              the day.
            </p>
            <p>
              The algorithm analyzes multiple factors with different weights
              based on their impact on stress:
            </p>
            <ul className="ml-4 space-y-2">
              <li className="flex items-start gap-2">
                <Heart className="text-chart-5 mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">Heart Rate</strong> -
                  Elevated resting heart rate often indicates higher stress
                  levels
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Activity className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">
                    Heart Rate Variability (HRV)
                  </strong>{" "}
                  - Lower HRV suggests your body is under stress and has reduced
                  recovery capacity
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Moon className="text-info mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">Sleep Quality</strong> -
                  Poor or insufficient sleep directly contributes to stress
                  accumulation
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Wind className="text-chart-2 mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">Breathing Rate</strong> -
                  Elevated breathing rate during sleep can indicate stress or
                  anxiety
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Thermometer className="text-chart-3 mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">Skin Temperature</strong>{" "}
                  - Variations from your baseline can signal stress responses
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Activity className="text-chart-1 mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong className="text-foreground">
                    Active Zone Minutes
                  </strong>{" "}
                  - Regular physical activity helps reduce stress levels
                </span>
              </li>
            </ul>
          </div>
        </Card>

        {/* Tips to Reduce Stress */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            Tips to Reduce Stress
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <TrendingDown className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                Practice deep breathing exercises for 5-10 minutes daily
              </span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingDown className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Maintain a consistent sleep schedule (7-9 hours)</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingDown className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                Get at least 150 minutes of moderate exercise per week
              </span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingDown className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Limit caffeine and alcohol intake</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingDown className="text-success mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Try meditation or mindfulness practices</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default StressScoreDetail;
